import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Modal } from "bootstrap"
import { disposeModal } from "../utils/modal-dispose.util"
import { base64urlToBuffer, bufferToBase64url, getCsrfToken } from "../utils/webauthn"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`. Both phrases
// come from the view (users/profile.html.erb) as translated copy, so the
// fallbacks stay empty — nothing can surface in a language the user did not
// pick, and showError() skips an empty message.
const DEFAULT_TRANSLATIONS = {
  duplicateMessage: "",
  genericMessage: ""
}

export default class extends Controller {
  static targets = [
    "registerButton",
    "nickname",
    "unsupported",
    "reverificationModal",
    "reverificationInput",
    "reverificationError",
    "reverificationSubmit"
  ]
  static values = {
    translations: { type: Object, default: {} },
    challengeUrl: String,
    createUrl: String,
    reverifyUrl: String
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    if (!window.PublicKeyCredential) {
      if (this.hasRegisterButtonTarget) this.registerButtonTarget.disabled = true
      if (this.hasUnsupportedTarget) this.unsupportedTarget.classList.remove("d-none")
      return
    }
    this.prefillDeviceName()
  }

  prefillDeviceName() {
    if (!this.hasNicknameTarget || this.nicknameTarget.value) return

    let name = ""
    if (navigator.userAgentData?.platform) {
      name = navigator.userAgentData.platform
      const brands = navigator.userAgentData.brands || []
      const browser = brands.find((b) => !b.brand.includes("Not") && !b.brand.includes("Chromium"))
      if (browser) name += ` ${browser.brand}`
    } else {
      const ua = navigator.userAgent
      if (ua.includes("iPhone")) name = "iPhone"
      else if (ua.includes("iPad")) name = "iPad"
      else if (ua.includes("Mac")) name = "Mac"
      else if (ua.includes("Windows")) name = "Windows"
      else if (ua.includes("Android")) name = "Android"
      else if (ua.includes("Linux")) name = "Linux"

      if (ua.includes("Chrome") && !ua.includes("Edg")) name += " Chrome"
      else if (ua.includes("Firefox")) name += " Firefox"
      else if (ua.includes("Safari") && !ua.includes("Chrome")) name += " Safari"
      else if (ua.includes("Edg")) name += " Edge"
    }

    this.nicknameTarget.value = name.trim() || "Passkey"
  }

  disconnect() {
    disposeModal(this.modal)
  }

  async register(event) {
    event.preventDefault()
    const button = this.registerButtonTarget
    button.disabled = true

    try {
      const needsReverification = await this.checkReverification()
      if (needsReverification) {
        this.showReverificationModal()
        return
      }

      await this.createPasskey()
    } catch (error) {
      if (error.name === "NotAllowedError") {
        // User cancelled the ceremony — no error to show
      } else if (error.name === "InvalidStateError") {
        this.showError(error.userMessage || this.#texts.duplicateMessage)
      } else {
        console.error("Passkey registration error:", error)
        this.showError(error.userMessage || this.#texts.genericMessage)
      }
    } finally {
      if (!this.modalShowing) button.disabled = false
    }
  }

  get nickname() {
    return this.hasNicknameTarget ? this.nicknameTarget.value : null
  }

  async checkReverification() {
    const response = await fetch(this.challengeUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: JSON.stringify({ nickname: this.nickname })
    })

    if (response.status === 403) return true
    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      const err = new Error("Challenge failed")
      err.userMessage = body.error
      throw err
    }

    this.pendingOptions = await response.json()
    return false
  }

  async confirmReverification(event) {
    event.preventDefault()
    if (this.hasReverificationSubmitTarget) this.reverificationSubmitTarget.disabled = true

    try {
      const input = this.reverificationInputTarget
      const body = {}

      if (input.type === "password") {
        body.password = input.value
      } else {
        body.otp_code = input.value
      }

      const response = await fetch(this.reverifyUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken()
        },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        if (this.hasReverificationErrorTarget) {
          const body = await response.json().catch(() => ({}))
          this.reverificationErrorTarget.textContent = body.error || this.#texts.genericMessage
          this.reverificationErrorTarget.classList.remove("d-none")
        }
        return
      }

      this.hideReverificationModal()
      await this.createPasskey()
    } catch (error) {
      console.error("Reverification error:", error)
      if (this.hasRegisterButtonTarget) this.registerButtonTarget.disabled = false
    } finally {
      if (this.hasReverificationSubmitTarget) this.reverificationSubmitTarget.disabled = false
    }
  }

  async createPasskey() {
    let options = this.pendingOptions
    this.pendingOptions = null

    if (!options) {
      const challengeResponse = await fetch(this.challengeUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken()
        },
        body: JSON.stringify({ nickname: this.nickname })
      })

      if (!challengeResponse.ok) {
        const body = await challengeResponse.json().catch(() => ({}))
        const err = new Error("Challenge failed")
        err.userMessage = body.error
        throw err
      }

      options = await challengeResponse.json()
    }

    options.challenge = base64urlToBuffer(options.challenge)
    options.user.id = base64urlToBuffer(options.user.id)
    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map((cred) => ({
        ...cred,
        id: base64urlToBuffer(cred.id)
      }))
    }

    const credential = await navigator.credentials.create({ publicKey: options })

    const createResponse = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken()
      },
      body: JSON.stringify({
        credential: {
          id: credential.id,
          rawId: bufferToBase64url(credential.rawId),
          type: credential.type,
          response: {
            attestationObject: bufferToBase64url(credential.response.attestationObject),
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            transports: credential.response.getTransports?.() || []
          }
        },
        nickname: this.hasNicknameTarget ? this.nicknameTarget.value : null
      })
    })

    if (!createResponse.ok) {
      const body = await createResponse.json().catch(() => ({}))
      const err = new Error("Registration failed")
      err.userMessage = body.error
      throw err
    }

    const result = await createResponse.json()
    Turbo.visit(result.redirect_to || window.location.href)
  }

  showReverificationModal() {
    if (!this.hasReverificationModalTarget) return
    this.modal = this.modal || new Modal(this.reverificationModalTarget)
    if (this.hasReverificationErrorTarget) {
      this.reverificationErrorTarget.classList.add("d-none")
    }
    if (this.hasReverificationInputTarget) {
      this.reverificationInputTarget.value = ""
    }
    this.modalShowing = true
    this.reverificationModalTarget.addEventListener(
      "hidden.bs.modal",
      () => {
        this.modalShowing = false
        if (this.hasRegisterButtonTarget) this.registerButtonTarget.disabled = false
      },
      { once: true }
    )
    this.modal.show()
  }

  hideReverificationModal() {
    this.modal?.hide()
  }

  showError(message) {
    const container = document.getElementById("toast_container")
    if (!container || !message) return

    const toast = document.createElement("div")
    toast.className = "toast"
    toast.setAttribute("role", "alert")
    toast.setAttribute("aria-live", "assertive")
    toast.setAttribute("aria-atomic", "true")
    toast.setAttribute("data-controller", "toast")

    const header = document.createElement("div")
    header.className = "toast-header text-white"

    const icon = document.createElement("span")
    icon.className = "material-symbols-outlined text-danger"
    icon.textContent = "error"

    const text = document.createElement("strong")
    text.className = "me-auto"
    text.textContent = message

    const closeBtn = document.createElement("button")
    closeBtn.type = "button"
    closeBtn.className = "btn-close btn-close-white"
    closeBtn.setAttribute("data-bs-dismiss", "toast")
    closeBtn.setAttribute("aria-label", "Close")

    header.append(icon, text, closeBtn)
    toast.appendChild(header)
    container.appendChild(toast)
  }
}
